import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { WebSocketContext } from "./WebSocketContext";
import { useTheme } from "./contexts/ThemeContext";
import StockCard from "./components/StockCard";
import "./StockList.css"; // Add CSS for the stock list
import BottomSheet from './components/BottomSheet';
import searchIcon from './assets/search.svg';
import {Search, LiveTv, CurrencyRupee, Add, Edit, Save, Cancel, Delete, FilterList, Close, Star, StarBorder} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Refresh } from '@mui/icons-material';
import { apiAppFetch, checkAppSession } from './utils/apiFetch';
import { loadFavourites } from './utils/favourites';
import { isMiniLot, watchlistStockId } from './utils/lotVariant';

const StockList = () => {
  const { stocks, userScripts, userDetails, lastUpdateTime, netPositions, fetchUserScriptsExternal, removeUserScriptFromList } = useContext(WebSocketContext);
  const { isDarkMode, toggleTheme } = useTheme();
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [filter, setFilter] = useState("FAVOURITE");
  const [favourites, setFavourites] = useState(() => loadFavourites());
  const [showMoneyOptions, setShowMoneyOptions] = useState(false);
  const [proView, setProView] = useState(() => {
    // Load view preference from localStorage; default to simple view (pro view off)
    const savedView = localStorage.getItem('proViewPreference');
    return savedView ? JSON.parse(savedView) : false;
  });
  const [oneClickTrade, setOneClickTrade] = useState(() => {
    // Load one click trade preference from localStorage
    const savedOneClick = localStorage.getItem('oneClickTradePreference');
    return savedOneClick ? JSON.parse(savedOneClick) : false;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [listFilter, setListFilter] = useState('');
  const [listSort, setListSort] = useState('');
  const navigate = useNavigate();
  const [showTv, setShowTv] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [reorderedScripts, setReorderedScripts] = useState([]);
  const [savedOrder, setSavedOrder] = useState({});
  const draggedItemRef = useRef(null);
  const touchDragIndexRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const syncFavourites = () => setFavourites(loadFavourites());
    window.addEventListener('favouritesUpdated', syncFavourites);
    window.addEventListener('focus', syncFavourites);
    return () => {
      window.removeEventListener('favouritesUpdated', syncFavourites);
      window.removeEventListener('focus', syncFavourites);
    };
  }, []);

  const handleNavigation = (path) => () => {
    navigate(path);
  };

  // Local storage functions for script order
  const saveScriptOrder = (order) => {
    try {
      localStorage.setItem('scriptOrder', JSON.stringify(order));
      console.log('Script order saved to localStorage:', order);
    } catch (error) {
      console.error('Error saving script order:', error);
    }
  };

  const loadScriptOrder = () => {
    try {
      const saved = localStorage.getItem('scriptOrder');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading script order:', error);
      return {};
    }
  };

  const getScriptOrder = (exchange) => {
    return savedOrder[exchange] || [];
  };

  const updateScriptOrder = (exchange, newOrder) => {
    const updatedOrder = {
      ...savedOrder,
      [exchange]: newOrder
    };
    setSavedOrder(updatedOrder);
    saveScriptOrder(updatedOrder);
  };

  // Function to toggle pro view and save preference
  const toggleProView = () => {
    const newProView = !proView;
    setProView(newProView);
    // Save preference to localStorage
    try {
      localStorage.setItem('proViewPreference', JSON.stringify(newProView));
      console.log('View preference saved:', newProView ? 'Pro View' : 'Simple View');
    } catch (error) {
      console.error('Error saving view preference:', error);
    }
  };

  // Function to toggle one click trade and save preference
  const toggleOneClickTrade = () => {
    const newOneClickTrade = !oneClickTrade;
    setOneClickTrade(newOneClickTrade);
    // Save preference to localStorage
    try {
      localStorage.setItem('oneClickTradePreference', JSON.stringify(newOneClickTrade));
      console.log('One Click Trade preference saved:', newOneClickTrade);
    } catch (error) {
      console.error('Error saving one click trade preference:', error);
    }
  };

  // Function to remove script from user's list
  const removeScriptFromUser = async (script) => {
    try {
      const response = await apiAppFetch('removeScriptFromUser', {
        method: 'POST',
        body: JSON.stringify({
          stockId: watchlistStockId(script),
        }),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      const data = await response.json();

      if (data.success) {
        removeUserScriptFromList(script.instrumentIdentifier, script.lotVariant);
        if (isEditMode) {
          setReorderedScripts((prev) =>
            prev.filter((item) => {
              if (String(item.instrumentIdentifier) !== String(script.instrumentIdentifier)) {
                return true;
              }
              return isMiniLot(item) !== isMiniLot(script);
            })
          );
        }
      } else {
        alert('Failed to remove script: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Error removing script: ' + error.message);
    }
  };

  // Function to handle script deletion
  const handleDeleteScript = (script, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${script.dispname}" from your watchlist?`
    );
    
    if (confirmDelete) {
      removeScriptFromUser(script);
    }
  };

  const handleToggle = () => {
    setShowTv(prevState => !prevState);
  };

  const handleCardClick = async (stock) => {
    const sessionOk = await checkAppSession();
    if (!sessionOk) {
      return;
    }

    setSelectedStock(stock);
    if(stock.exchange=="OPTSTK"){
      stock.intraday = userDetails.OPTSTK_intraday;
    } else if(stock.exchange=="FUTSTK"){
      stock.intraday = userDetails.FUTSTK_intraday;
    } else if(stock.exchange=="FUTCOM"){
      stock.intraday = userDetails.FUTCOM_intraday;
    }
    setShowBottomSheet(true);
  };

  const handleCloseSheet = () => {
    setShowBottomSheet(false);
    setSelectedStock(null);
    // alert("closed"+showBottomSheet);
  };

  // Function to get the latest stock data for the selected stock
  const getLatestStockData = (stock) => {
    return stocks[stock.instrument_token] || {};
  };

  // Effect to update the selected stock data in the bottom sheet whenever stocks context updates
  useEffect(() => {
    if (selectedStock) {
      const latestStockData = getLatestStockData(selectedStock);
      setSelectedStock((prevStock) => ({
        ...prevStock,
        live: latestStockData,
      }));
    }
  }, [stocks]);

  // Effect to load saved script order on component mount
  useEffect(() => {
    const loadedOrder = loadScriptOrder();
    setSavedOrder(loadedOrder);
  }, []);

  // Effect to listen for edit mode toggle from TitleBar
  useEffect(() => {
    const handleEditModeToggle = () => {
      handleEditMode();
    };
    const handleChangeView = (event) => {
      if (event?.detail && typeof event.detail.proView === 'boolean') {
        setProView(event.detail.proView);
        localStorage.setItem('proViewPreference', JSON.stringify(event.detail.proView));
        return;
      }
      toggleProView();
    };

    window.addEventListener('toggleEditMode', handleEditModeToggle);
    window.addEventListener('changeView', handleChangeView);
    return () => {
      window.removeEventListener('toggleEditMode', handleEditModeToggle);
      window.removeEventListener('changeView', handleChangeView);
    };
  }, [proView]);

  // Listen for trade bottom sheet events from sticky scripts
  useEffect(() => {
    const handleOpenTradeBottomSheet = async (event) => {
      const { script } = event.detail;
      if (!script) {
        return;
      }

      const sessionOk = await checkAppSession();
      if (!sessionOk) {
        return;
      }

      // Get live data from stocks context
      const liveData = stocks[script.instrument_token] || {};
      
      // Create a mock stock object for the bottom sheet with proper structure
      const mockStock = {
        ...script,
        // Basic stock properties
        buyPrice: liveData.Bid || script.buyPrice || 0,
        sellPrice: liveData.Ask || script.sellPrice || 0,
        open: liveData.Open || script.open || 0,
        high: liveData.High || script.high || 0,
        low: liveData.Low || script.low || 0,
        close: liveData.Close || script.close || 0,
        lastTradedPrice: liveData.LTP || script.lastTradedPrice || 0,
        // Live data object that BottomSheet expects
        live: {
          Bid: liveData.Bid || 0,
          Ask: liveData.Ask || 0,
          Open: liveData.Open || 0,
          High: liveData.High || 0,
          Low: liveData.Low || 0,
          Close: liveData.Close || 0,
          LTP: liveData.LTP || 0,
          Prev_Close: liveData.Prev_Close || liveData.Close || 0
        }
      };
      setSelectedStock(mockStock);
      setShowBottomSheet(true);
    };

    window.addEventListener('openTradeBottomSheet', handleOpenTradeBottomSheet);
    return () => {
      window.removeEventListener('openTradeBottomSheet', handleOpenTradeBottomSheet);
    };
  }, [stocks]);

  // Filtered user scripts based on the selected filter and search query
  const filteredUserScripts = Object.values(userScripts).filter((stock) => {
    // Filter by exchange
    let matchesFilter = false;
    if (filter === "FAVOURITE") {
      matchesFilter = favourites.has(watchlistStockId(stock));
    } else if (filter === "MCX FUT") {
      matchesFilter = stock.exchange === "FUTCOM";
    } else if (filter === "NSE FUT") {
      matchesFilter = stock.exchange === "FUTSTK";
    } else if (filter === "NSE OPT") {
      matchesFilter = stock.exchange === "OPTSTK";
    } else {
      matchesFilter = true;
    }
    
    // Filter by search query
    const matchesSearch = searchQuery === "" || 
      (stock.dispname && stock.dispname.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (stock.symbol && stock.symbol.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (!matchesFilter || !matchesSearch) return false;

    if (listFilter === 'gain' || listFilter === 'loss' || listFilter === 'changes') {
      const live = stocks[stock.instrument_token] || {};
      const ltp = Number(live.LTP ?? stock.lastTradedPrice ?? 0);
      const prev = Number(live.Prev_Close ?? live.Close ?? stock.close ?? 0);
      const change = ltp - prev;
      if (listFilter === 'gain') return change > 0;
      if (listFilter === 'loss') return change < 0;
      return change !== 0;
    }

    return true;
  });

  // Sort filtered scripts according to saved order
  const getSortedScripts = (scripts) => {
    const exchange = filter === "FAVOURITE" ? "FAVOURITE" :
                    filter === "MCX FUT" ? "FUTCOM" : 
                    filter === "NSE FUT" ? "FUTSTK" : 
                    filter === "NSE OPT" ? "OPTSTK" : "ALL";
    
    const savedOrderForExchange = getScriptOrder(exchange);
    
    if (listSort === 'alpha') {
      return [...scripts].sort((a, b) => String(a.dispname || '').localeCompare(String(b.dispname || '')));
    }

    if (listSort === 'price') {
      return [...scripts].sort((a, b) => {
        const aLive = stocks[a.instrument_token] || {};
        const bLive = stocks[b.instrument_token] || {};
        const aPrice = Number(aLive.LTP ?? a.lastTradedPrice ?? 0);
        const bPrice = Number(bLive.LTP ?? b.lastTradedPrice ?? 0);
        return bPrice - aPrice;
      });
    }

    if (savedOrderForExchange.length === 0) {
      return [...scripts];
    }

    // Create a map for quick lookup
    const orderMap = {};
    savedOrderForExchange.forEach((scriptId, index) => {
      orderMap[scriptId] = index;
    });

    // Sort scripts according to saved order (copy first — don't mutate source)
    return [...scripts].sort((a, b) => {
      const aOrder = orderMap[watchlistStockId(a)];
      const bOrder = orderMap[watchlistStockId(b)];
      
      // If both scripts are in saved order, sort by their order
      if (aOrder !== undefined && bOrder !== undefined) {
        return aOrder - bOrder;
      }
      
      // If only one script is in saved order, prioritize it
      if (aOrder !== undefined) return -1;
      if (bOrder !== undefined) return 1;
      
      // If neither script is in saved order, maintain original order
      return 0;
    });
  };

  const sortedFilteredScripts = getSortedScripts(filteredUserScripts);

  // Use reordered scripts if in edit mode, otherwise use sorted filtered scripts
  const displayScripts = isEditMode && reorderedScripts.length > 0 ? reorderedScripts : sortedFilteredScripts;

  // Reset edit-mode list when filter/search changes (not on every isEditMode toggle)
  useEffect(() => {
    if (isEditMode) {
      setReorderedScripts(getSortedScripts(filteredUserScripts));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchQuery]);

  const positionQtyByStockId = useMemo(() => {
    const map = {};
    (netPositions || []).forEach((position) => {
      const netQty = Number(position.netQty || 0);
      if (!netQty) {
        return;
      }
      const stockId = String(position.stockId || position.instrumentIdentifier || '');
      if (!stockId) {
        return;
      }
      const variant = String(position.lotVariant || 'MEGA').toUpperCase() === 'MINI' ? 'MINI' : 'MEGA';
      map[`${stockId}|${variant}`] = {
        qty: Math.abs(netQty),
        side: position.buyQty > 0 ? 'Buy' : 'Sell',
      };
    });
    return map;
  }, [netPositions]);

  const getOpenPositionForStock = (stock) => {
    const stockId = String(stock?.instrumentIdentifier || '');
    const variant = String(stock?.lotVariant || 'MEGA').toUpperCase() === 'MINI' ? 'MINI' : 'MEGA';
    return stockId ? positionQtyByStockId[`${stockId}|${variant}`] || null : null;
  };

  const handleRefresh = () => {
    fetchUserScriptsExternal();
    //alert("Refresh");
    //refreshTradesAndPortfolio("trades");
  };

  const moveScript = (fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === undefined || toIndex === null || toIndex === undefined) {
      return;
    }
    if (fromIndex === toIndex) {
      return;
    }

    setReorderedScripts((prev) => {
      const source = prev.length > 0 ? prev : sortedFilteredScripts;
      const newOrder = [...source];
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= newOrder.length ||
        toIndex >= newOrder.length
      ) {
        return prev.length > 0 ? prev : [...sortedFilteredScripts];
      }
      const [draggedScript] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, draggedScript);
      return newOrder;
    });
  };

  const clearDragState = () => {
    draggedItemRef.current = null;
    touchDragIndexRef.current = null;
    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Desktop HTML5 drag and drop
  const handleDragStart = (e, index) => {
    draggedItemRef.current = index;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverItem !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragLeave = (e) => {
    // Ignore leave events when moving over child elements
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverItem(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const fromData = e.dataTransfer.getData('text/plain');
    const fromIndex = fromData !== '' ? Number(fromData) : draggedItemRef.current;
    moveScript(fromIndex, dropIndex);
    clearDragState();
  };

  const handleDragEnd = () => {
    clearDragState();
  };

  // Mobile / touch reorder via drag handle
  const getTouchTargetIndex = (clientY) => {
    const items = document.querySelectorAll('.quotes-list .drag-item');
    for (let i = 0; i < items.length; i += 1) {
      const rect = items[i].getBoundingClientRect();
      if (clientY >= rect.top && clientY <= rect.bottom) {
        return i;
      }
    }
    return null;
  };

  const handleTouchStart = (e, index) => {
    if (!isEditMode) {
      return;
    }

    touchDragIndexRef.current = index;
    draggedItemRef.current = index;
    setDraggedItem(index);

    const onTouchMove = (moveEvent) => {
      if (touchDragIndexRef.current === null) {
        return;
      }
      const touch = moveEvent.touches[0];
      if (!touch) {
        return;
      }
      moveEvent.preventDefault();
      const overIndex = getTouchTargetIndex(touch.clientY);
      if (overIndex !== null) {
        setDragOverItem(overIndex);
      }
    };

    const onTouchEnd = (endEvent) => {
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);

      if (touchDragIndexRef.current === null) {
        return;
      }

      const touch = endEvent.changedTouches && endEvent.changedTouches[0];
      const fromIndex = touchDragIndexRef.current;
      const toIndex = touch ? getTouchTargetIndex(touch.clientY) : null;
      moveScript(fromIndex, toIndex);
      clearDragState();
    };

    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);
  };

  const handleEditMode = () => {
    if (!isEditMode) {
      setReorderedScripts([...sortedFilteredScripts]);
      setIsEditMode(true);
      return;
    }
    setIsEditMode(false);
    setReorderedScripts([]);
    clearDragState();
  };

  const handleSaveOrder = () => {
    // Save the new order to localStorage
    const exchange = filter === "FAVOURITE" ? "FAVOURITE" :
                    filter === "MCX FUT" ? "FUTCOM" : 
                    filter === "NSE FUT" ? "FUTSTK" : 
                    filter === "NSE OPT" ? "OPTSTK" : "ALL";
    
    const scriptIds = reorderedScripts.map((script) => watchlistStockId(script));
    updateScriptOrder(exchange, scriptIds);
    
    console.log('Saving new order for', exchange, ':', scriptIds);
    setIsEditMode(false);
    setReorderedScripts([]);
    clearDragState();
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setReorderedScripts([]);
    clearDragState();
  };

  return (
    <div className={`quotes-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {showTv && <div className="video-container">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/4QvoTo1CFPs?si=A2GdO2Akv5IumonS"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </div>}

      {/* Control Panel */}
      <div className="control-panel">
        <div className="search-filter-row">
          <div className="search-container">
            <Search style={{ height: '20px', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search by Script Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {showFilterSheet && (
        <div className="quotes-filter-overlay" onClick={() => setShowFilterSheet(false)}>
          <div className="quotes-filter-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="quotes-filter-head">
              <h3>Filters</h3>
              <button
                type="button"
                className="quotes-filter-clear"
                onClick={() => {
                  setListFilter('');
                  setListSort('');
                }}
              >
                Clear
              </button>
            </div>
            <div className="quotes-filter-chips">
              {[
                { value: 'changes', label: 'Daily Changes' },
                { value: 'gain', label: 'Daily gain' },
                { value: 'loss', label: 'Daily loss' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`quotes-filter-chip${listFilter === option.value ? ' active' : ''}`}
                  onClick={() => setListFilter(listFilter === option.value ? '' : option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="quotes-filter-divider" />
            <h3>Sort</h3>
            <button
              type="button"
              className={`quotes-sort-item${listSort === 'alpha' ? ' active' : ''}`}
              onClick={() => setListSort(listSort === 'alpha' ? '' : 'alpha')}
            >
              <span className="quotes-sort-icon">A-Z</span>
              Alphabetically
            </button>
            <button
              type="button"
              className={`quotes-sort-item${listSort === 'price' ? ' active' : ''}`}
              onClick={() => setListSort(listSort === 'price' ? '' : 'price')}
            >
              <CurrencyRupee className="quotes-sort-icon-svg" />
              Price
            </button>
            <button type="button" className="quotes-filter-done" onClick={() => setShowFilterSheet(false)}>
              <Close />
              Done
            </button>
          </div>
        </div>
      )}

      <div className="quotes-container">
        <div className="quotes-tab-row">
          <div className="filter-tabs">
            {[
              { value: 'FAVOURITE', label: 'Favourite' },
              { value: 'MCX FUT', label: 'MCX FUT' },
              { value: 'NSE FUT', label: 'NSE FUT' },
              { value: 'NSE OPT', label: 'NSE OPT' }
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`filter-tab ${filter === tab.value ? 'active' : ''}`}
                onClick={() => setFilter(tab.value)}
              >
                {tab.value === 'FAVOURITE' && <Star />}
                {tab.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`quotes-filter-btn${listFilter || listSort ? ' active' : ''}`}
            onClick={() => setShowFilterSheet(true)}
          >
            <FilterList />
          </button>
        </div>

        {isEditMode && (
          <div className="edit-mode-controls">
            <button className="save-btn" onClick={handleSaveOrder}>
              <Save style={{ height: '16px', marginRight: '8px' }} />
              Save Order
            </button>
            <button className="cancel-btn" onClick={handleCancelEdit}>
              <Cancel style={{ height: '16px', marginRight: '8px' }} />
              Cancel
            </button>
          </div>
        )}

        {displayScripts.length === 0 ? (
          <div className={`empty-state${filter === 'FAVOURITE' ? ' empty-favourites' : ''}`}>
            {filter === 'FAVOURITE' ? (
              <>
                <div className="empty-state-icon-wrap">
                  <StarBorder className="empty-state-icon" />
                </div>
                <h3>No favourites yet</h3>
                <p>Star scripts while adding them to keep your Favourite list here.</p>
                <button type="button" className="add-script-btn" onClick={handleNavigation('/addScript')}>
                  <Add />
                  Add Script
                </button>
              </>
            ) : (
              <>
                <h3>No scripts in this list</h3>
                <p>Add your desired scripts to get started.</p>
                <button type="button" className="add-script-btn" onClick={handleNavigation('/addScript')}>
                  <Add />
                  Add Script
                </button>
              </>
            )}
          </div>
        ) : (
          <div className={`quotes-list ${isEditMode ? 'edit-mode' : ''}`}>
            {displayScripts.map((stock, index) => {
              const _stock = stocks[stock.instrument_token] || {};
              return (
                <div
                  key={watchlistStockId(stock)}
                  data-drag-index={index}
                  draggable={isEditMode}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`drag-item ${draggedItem === index ? 'dragging' : ''} ${dragOverItem === index ? 'drag-over' : ''}`}
                  style={{ 
                    cursor: isEditMode ? 'move' : 'default',
                    userSelect: isEditMode ? 'none' : 'auto',
                    touchAction: isEditMode ? 'none' : 'auto'
                  }}
                >
                  {isEditMode && (
                    <>
                      <div 
                        className="drag-handle" 
                        title="Drag to reorder"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                      >
                        <span>⋮⋮</span>
                      </div>
                      <button
                        className="delete-button"
                        title="Remove from watchlist"
                        onClick={(e) => handleDeleteScript(stock, e)}
                      >
                        <Delete style={{ fontSize: '18px' }} />
                      </button>
                    </>
                  )}
                  <div 
                    style={{ 
                      marginLeft: isEditMode ? '30px' : '0px',
                      marginRight: isEditMode ? '0px' : '0px',
                      transition: 'margin 0.2s ease'
                    }}
                  >
                    <StockCard 
                      _stock={_stock} 
                      stock={stock}
                      openPosition={getOpenPositionForStock(stock)}
                      onCardClick={isEditMode ? () => {} : handleCardClick}
                      isDarkMode={isDarkMode}
                      proView={proView}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Navigation is handled by NavBar.js */}

      {selectedStock && <BottomSheet isOpen={showBottomSheet} onClose={handleCloseSheet} userDetails={userDetails} stock={selectedStock} />}
    </div>
  );
};

export default StockList;
